<template>
  <div data-testid="so-orders-table">
    <data-table
      ref="dataTableRef"
      :config="dataTableConfig"
      :data="decoratedRows"
      :autoLoad="false"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @refresh="$emit('refresh')"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useSOStatusMap } from '../../composables/useSOStatusMap';
import type { SOOrderFlatRow } from '../../composables/useSOOrders';

interface DataTableInstance {
  loadData: () => void;
}

interface BadgeConfig {
  label: string;
  severity: string;
  tooltip?: string;
}

const props = defineProps<{
  data: SOOrderFlatRow[];
  loading?: boolean;
}>();

defineEmits<{
  refresh: [];
}>();

const { t, n } = useI18n();
const {
  getOrderStatus,
  orderStatusMap,
  getFinancialStatus,
  financialStatusMap,
  getItemStatus,
  itemStatusMap,
  prazoLabel,
  prazoSeverity,
  daysUntilDueText,
} = useSOStatusMap();

const dataTableRef = ref<DataTableInstance | null>(null);

// Decorate rows with computed display fields. Each row = one OS.
const decoratedRows = computed(() =>
  props.data.map((row) => {
    const phone = `${row.cliente_ddd ?? ''} ${row.cliente_telefone ?? ''}`.trim();
    const doc = row.cliente_cpf
      ? `${row.cliente_document_type ?? t('Documento')}: ${row.cliente_cpf}`
      : '';
    return {
      ...row,
      _so_order_label: `${t('OS')} ${row.so_order_id}`,
      _items_count_label: `${t('Itens: ')}${row.so_order_items_count ?? 0}`,
      _cliente_phone: phone,
      _cliente_doc: doc,
    };
  })
);

const tableColumns = computed(() => [
  {
    data: '_so_order_label',
    sortField: 'so_order_id',
    title: t('OS'),
    width: 130,
    sortable: true,
    renderType: 'custom_clickable_id',
    clickCallback: (_value: unknown, row?: Record<string, unknown>) => {
      const orderId = row?.so_order_id;
      if (orderId !== undefined) {
        eventBus.emit('modal.pdv-checkout.open', { orderId });
      }
    },
    mergedColumns: [
      { data: 'franquia', title: t('Franquia') },
      { data: '_items_count_label', title: t('Itens') },
    ],
  },
  {
    data: 'so_order_status',
    title: t('Status'),
    width: 130,
    sortable: true,
    filterType: 'select',
    filterOptions: Object.entries(orderStatusMap as Record<string, { label: string }>).map(([value, { label }]) => ({ value, label })),
    renderType: 'badge',
    badgeConfig: (row: Record<string, unknown>): BadgeConfig => {
      const mapped = getOrderStatus(row.so_order_status);
      return { label: mapped.label, severity: mapped.severity, tooltip: mapped.label };
    },
  },
  {
    data: 'so_order_financial_status',
    title: t('Pagamento'),
    width: 120,
    sortable: true,
    filterType: 'select',
    filterOptions: Object.entries(financialStatusMap as Record<string, { label: string }>).map(([value, { label }]) => ({ value, label })),
    renderType: 'badge',
    badgeConfig: (row: Record<string, unknown>): BadgeConfig => {
      const mapped = getFinancialStatus(row.so_order_financial_status);
      return { label: mapped.label, severity: mapped.severity, tooltip: mapped.label };
    },
  },
  {
    data: 'so_order_total',
    title: t('Valor'),
    width: 170,
    sortable: true,
    renderType: 'currency',
    mergedColumns: [
      {
        data: 'so_order_total_recebido',
        title: t('Recebido'),
        cellRenderer: ({ data }: { data: Record<string, unknown> }): string => {
          const received = Number(data.so_order_total_recebido || 0);
          return `${t('Recebido')}: ${n(received, 'currency')}`;
        },
      },
      {
        data: 'so_order_total_pendente',
        title: t('Pendente'),
        cellRenderer: ({ data }: { data: Record<string, unknown> }): string => {
          const pending = Number(data.so_order_total_pendente || 0);
          return `${t('Pendente')}: ${n(pending, 'currency')}`;
        },
      },
    ],
  },
  {
    data: 'so_order_client_name',
    title: t('Cliente'),
    width: 220,
    sortable: true,
    filterType: 'text',
    mergedColumns: [
      { data: '_cliente_phone', title: t('Telefone') },
      { data: 'cliente_email', title: t('Email') },
      { data: '_cliente_doc', title: t('Documento') },
    ],
  },
  {
    data: 'whatsapp',
    title: t('WhatsApp'),
    width: 130,
    sortable: false,
    cellRenderer: (params: { data: Record<string, unknown> }): string => {
      if (params.data._isFrozenFooter) return '';
      const ddi = String(params.data.cliente_ddi ?? '').replace(/\D/g, '');
      const ddd = String(params.data.cliente_ddd ?? '').replace(/\D/g, '');
      const phone = String(params.data.cliente_telefone ?? '').replace(/\D/g, '');
      if (!phone) return '';
      return `<a class="p-button p-button-success p-button-sm whatsapp-link" href="https://api.whatsapp.com/send?phone=${ddi}${ddd}${phone}" target="_blank" title="${t('WhatsApp')}"><i class="pi pi-whatsapp"></i> ${t('Enviar')}</a>`;
    },
  },

]);

const dataTableConfig = computed(() => ({
  tableId: 'tabelaOrdensServico',
  filename: `ordens_servico_${new Date().toISOString().split('T')[0]}`,
  columns: tableColumns.value,
  defaultSortField: 'so_order_id',
  defaultSortOrder: 'desc',
  pageLength: 50,
  pagination: true,
  rowSelection: 'single',
  resizableColumns: true,
  showFilters: true,
  filterDisplay: 'row',
  stripedRows: true,
  showGridlines: true,
  size: 'small',
}));

const refreshButtonConfig = computed(() => ({
  show: true,
  text: t('Atualizar'),
  loading: props.loading ?? false,
}));

const csvButtonConfig = computed(() => ({
  show: true,
  text: t('CSV'),
}));
</script>

<style scoped>
:deep(.whatsapp-link) {
  text-decoration: none;
  white-space: nowrap;
}
:deep(.whatsapp-link:hover) {
  opacity: 0.9;
}
</style>
